/**
 * Zod schema for `GET /api/admin/sending-domains`
 * (`apps/workers/src/api/admin/sendingDomains.ts`) — the active
 * cold-outreach rotation domains and their per-domain SPF/DKIM/DMARC
 * verification status.
 *
 * GitHub issue "[Email] Move cold outreach off the brand domain —
 * dedicated, warmed, rotated sending domains" (#188/#516), milestone
 * "Email & Outreach Overhaul".
 *
 * Mirrors `AdminSendingDomainRow` / `AdminSendingDomainsResponse`
 * (`apps/workers/src/api/admin/sendingDomains.ts`) field-for-field — see
 * `@/lib/validation/subscriptions.ts`'s header comment for why this
 * workspace duplicates rather than cross-imports the backend's raw
 * TypeScript interfaces.
 */

import { z } from 'zod';

export const sendingDomainAuthTriStateSchema = z.enum(['pass', 'fail', 'unknown']);
export type SendingDomainAuthTriState = z.infer<typeof sendingDomainAuthTriStateSchema>;

export const adminSendingDomainRowSchema = z.object({
  domain: z.string(),
  status: z.string(),
  spf: sendingDomainAuthTriStateSchema,
  dkim: sendingDomainAuthTriStateSchema,
  dmarc: sendingDomainAuthTriStateSchema,
  auth_check_status: sendingDomainAuthTriStateSchema,
  auth_checked_at: z.string().nullable(),
  in_active_rotation: z.boolean(),
  excluded_reason: z.string().nullable(),
  is_brand_domain: z.boolean(),
});
export type AdminSendingDomainRow = z.infer<typeof adminSendingDomainRowSchema>;

export const adminSendingDomainsResponseSchema = z.object({
  data: z.array(adminSendingDomainRowSchema),
  active_rotation_count: z.number().int().nonnegative(),
  generated_at: z.string(),
});
export type AdminSendingDomainsResponse = z.infer<typeof adminSendingDomainsResponseSchema>;
